import {
  BellOutlined,
  CheckOutlined,
  MoonOutlined,
  SunOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import {
  Avatar,
  Badge,
  Button,
  Divider,
  Dropdown,
  Empty,
  List,
  Popover,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useNotificationStream } from '@/features/notifications/hooks/use-notification-stream';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useProgressRouter } from '@/hooks/use-progress-router';
import { useUrlPrefix } from '@/hooks/use-url-prefix';
import { useAuth } from '@/lib/auth';

function getNotificationMessage(
  type: string,
  data: any,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  switch (type) {
    case 'TASK_CREATED':
      return t('navigation.notificationMessages.taskCreated');
    case 'MENTION':
      return t('navigation.notificationMessages.mention', {
        author:
          data?.authorName ?? t('navigation.notificationMessages.someone'),
      });
    default:
      return t('navigation.notificationMessages.newNotification');
  }
}

function getNotificationLink(type: string, data: any): string {
  switch (type) {
    case 'TASK_CREATED':
      return `tasks/${data?.taskId}`;
    case 'MENTION':
      if (data?.resourceType === 'Task') return `tasks/${data?.resourceId}`;
      if (data?.resourceType === 'Flight')
        return `flight-list/flights/${data?.resourceId}`;
      return '#';
    default:
      return '#';
  }
}

function getNotificationIcon(type: string): React.ReactNode {
  switch (type) {
    case 'TASK_CREATED':
      return (
        <Avatar size="small" style={{ backgroundColor: '#1677ff' }}>
          <CheckOutlined />
        </Avatar>
      );
    case 'MENTION':
      return (
        <Avatar size="small" style={{ backgroundColor: '#52c41a' }}>
          @
        </Avatar>
      );
    default:
      return (
        <Avatar size="small" style={{ backgroundColor: '#faad14' }}>
          <BellOutlined />
        </Avatar>
      );
  }
}

function formatTimeAgo(
  dateStr: string | Date,
  t: (key: string, params?: Record<string, unknown>) => string,
): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return t('navigation.timeAgo.justNow');
  if (diffMinutes < 60)
    return t('navigation.timeAgo.minutesAgo', { minutes: diffMinutes });

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24)
    return t('navigation.timeAgo.hoursAgo', { hours: diffHours });

  const diffDays = Math.floor(diffHours / 24);
  return t('navigation.timeAgo.daysAgo', { days: diffDays });
}

const MAX_VISIBLE_NOTIFICATIONS = 5;

export function NotificationBellDropdown() {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const decorateUrl = useUrlPrefix();
  const router = useProgressRouter();
  const { notifications, unreadCount, isLoading, markAsRead, markAllAsRead } =
    useNotificationStream();

  const visibleNotifications = notifications.slice(
    0,
    MAX_VISIBLE_NOTIFICATIONS,
  );

  const content = (
    <div style={{ width: 360 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 16px',
        }}
      >
        <Typography.Text strong>
          {t('navigation.notifications')}
        </Typography.Text>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              markAllAsRead();
            }}
          >
            {t('navigation.markAllAsRead')}
          </Button>
        )}
      </div>
      <Divider style={{ margin: 0 }} />
      {isLoading ? (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('navigation.noNotifications')}
          style={{ padding: '16px 0' }}
        />
      ) : (
        <>
          <List
            dataSource={visibleNotifications}
            renderItem={(notification) => {
              const data = notification.data as any;
              const message = getNotificationMessage(
                notification.type,
                data,
                t,
              );
              const link = getNotificationLink(notification.type, data);
              const icon = getNotificationIcon(notification.type);

              return (
                <List.Item
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    backgroundColor: !notification.read
                      ? 'rgba(22, 119, 255, 0.04)'
                      : undefined,
                  }}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.id);
                    }
                    setOpen(false);
                    router.push(decorateUrl(link));
                  }}
                >
                  <List.Item.Meta
                    avatar={icon}
                    title={
                      <Typography.Text
                        style={{
                          fontSize: 13,
                          fontWeight: !notification.read ? 600 : 400,
                        }}
                      >
                        {message}
                      </Typography.Text>
                    }
                    description={
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {formatTimeAgo(notification.createdAt, t)}
                      </Typography.Text>
                    }
                  />
                  {!notification.read && (
                    <Badge status="processing" style={{ marginLeft: 8 }} />
                  )}
                </List.Item>
              );
            }}
          />
          {notifications.length > MAX_VISIBLE_NOTIFICATIONS && (
            <>
              <Divider style={{ margin: 0 }} />
              <div style={{ padding: '8px 16px', textAlign: 'center' }}>
                <Link
                  to={decorateUrl('notifications')}
                  onClick={() => setOpen(false)}
                >
                  <Button type="link" size="small">
                    {t('navigation.viewAllNotifications')}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      arrow={false}
      overlayInnerStyle={{ padding: 0 }}
    >
      <Tooltip title={t('navigation.notifications')}>
        <button
          aria-label={t('navigation.notifications')}
          className="flex cursor-pointer items-center justify-center rounded-lg p-2 text-gray-200 hover:bg-gray-700 hover:text-white"
        >
          <Badge count={unreadCount} size="small">
            <BellOutlined className="text-xl text-gray-200" />
          </Badge>
        </button>
      </Tooltip>
    </Popover>
  );
}

export function ThemeToggle({
  isDark,
  onToggle,
}: {
  isDark: boolean;
  onToggle: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={
        isDark
          ? t('navigation.switchToLightMode')
          : t('navigation.switchToDarkMode')
      }
    >
      <Switch
        checked={isDark}
        onChange={onToggle}
        checkedChildren={<MoonOutlined />}
        unCheckedChildren={<SunOutlined />}
      />
    </Tooltip>
  );
}

export function UserMenuDropdown() {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const { logout, profile } = useAuth();

  const handleSignOut = async () => {
    await logout();
  };

  // Use currentUser from API, fall back to JWT profile for name display
  const displayName = currentUser
    ? `${currentUser.firstName} ${currentUser.lastName}`
    : profile
      ? `${profile.given_name ?? ''} ${profile.family_name ?? ''}`.trim()
      : '';

  const userMenuItems: MenuProps['items'] = [
    {
      type: 'group',
      label: displayName || t('navigation.user'),
      children: [
        {
          key: 'user-settings',
          label: (
            <Link to="/settings/profile">{t('navigation.userSettings')}</Link>
          ),
        },
      ],
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'signout',
      label: t('navigation.signOut'),
      onClick: handleSignOut,
    },
  ];

  if (!currentUser && !profile) return null;

  const initials = displayName
    ? displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '';

  return (
    <Dropdown
      menu={{ items: userMenuItems }}
      trigger={['click']}
      placement="bottomRight"
    >
      <button className="flex cursor-pointer items-center gap-2 rounded-lg p-1 text-gray-200 hover:bg-gray-700 hover:text-white">
        <Avatar data-testid="user-menu-avatar" size={32}>
          {initials}
        </Avatar>
        <span className="hidden text-sm font-medium sm:inline">
          {displayName}
        </span>
      </button>
    </Dropdown>
  );
}

export function NavbarRightSection() {
  return (
    <Space size="middle">
      <NotificationBellDropdown />
      <ThemeToggleWithState />
      <UserMenuDropdown />
    </Space>
  );
}

function ThemeToggleWithState() {
  const [isDark, setIsDark] = React.useState(false);

  React.useEffect(() => {
    const darkMode = document.documentElement.classList.contains('dark');
    setIsDark(darkMode);
  }, []);

  function handleThemeToggle(checked: boolean) {
    if (checked) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('tenant-ui-theme-mode', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('tenant-ui-theme-mode', 'light');
    }
    setIsDark(checked);
  }

  return <ThemeToggle isDark={isDark} onToggle={handleThemeToggle} />;
}
